/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/presentationList.config.json";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import {
  GET_COSTUMETYPES,
  GET_PRESENTATION_LIST,
} from "../graphql/getPresentationList.query";
import { VApplication } from "../../../application";

const is = require("is_js");

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IPresentationListMethods extends IPluginMethodMap {
  getPresentationListProps(args: any): Promise<any>;
}

export class PresentationListService implements IAgentPlugin {
  readonly methods: IPresentationListMethods;

  constructor() {
    this.methods = {
      getPresentationListProps: this.getPresentationListProps.bind(this),
    };
  }

  async getPresentationListProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    let filter_apply: any = {};

    if (args?.filter && is.existy(args?.filter)) {
      filter_apply = this.convertCriteria(args?.filter);
    }

    try {
      if (!client) {
        return {
          ...config,
          ultUsers: [],
          isEmpty: true,
          presentations: [],
          presentationOptions: [],
          activePresentation: null,
          activePresentationId: null,
          selectedPresentationId: null,
          currentPage: 1,
          activePage: 1,
          pageSize: args?.size ?? 10,
          totalCount: 0,
          totalPages: 1,
        };
      }

      const presentationRes = await client.query({
        query: GET_PRESENTATION_LIST,
        variables: {
          options: {
            limit: 100,
            skip: 0,
            sort: {
              order: "asc",
              field: "status",
            },
          },
          filter: {},
        },
        fetchPolicy: "network-only",
      });

      const presentationPayload = presentationRes?.data?.getPresentations;

      const presentations = Array.isArray(presentationPayload?.content)
        ? presentationPayload.content
        : [];

      const activePresentation =
        presentations.find(
          (item: any) => String(item?.status).toLowerCase() === "active",
        ) ??
        presentations?.[0] ??
        null;

      const activePresentationId = activePresentation?.id ?? null;

      const presentationOptions = presentations.map((item: any) => ({
        label: item?.name ?? "-",
        value: item?.id,
        status: item?.status,
        year: item?.year,
        country: item?.country,
        type: item?.type,
      }));

      const selectedPresentationId =
        args?.presentationId ??
        args?.filter?.presentation ??
        args?.filter?.presentationId ??
        activePresentationId;

      if (selectedPresentationId) {
        filter_apply.presentation = {
          eq: selectedPresentationId,
        };
      }

      const costumeTypeRes = await client.query({
        query: GET_COSTUMETYPES,
        variables: {
          options: {
            limit: args?.size ?? 10,
            skip: args?.offset ?? 0,
            sort: {
              order: "asc",
              field: "status",
            },
          },
          filter: filter_apply,
        },
        fetchPolicy: "network-only",
      });

      const costumeTypePayload = costumeTypeRes?.data?.getCostumeTypes;

      const totalCount = costumeTypePayload?.count ?? 0;
      const limit = costumeTypePayload?.limit ?? args?.size ?? 10;
      const skip = costumeTypePayload?.skip ?? args?.offset ?? 0;

      const totalPages = totalCount ? Math.ceil(totalCount / limit) : 1;
      const currentPage = limit ? skip / limit + 1 : 1;

      const rowData = this.formatCostumeTypeTable(
        costumeTypePayload?.content ?? [],
      );

      return {
        ...config,

        ultUsers: rowData,
        isEmpty: totalCount <= 0,

        presentations,
        presentationOptions,
        activePresentation,
        activePresentationId,
        selectedPresentationId,

        currentPage,
        activePage: currentPage,
        pageSize: limit,
        totalCount,
        totalPages,
      };
    } catch (err: any) {
      const displayError =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);

      if (displayError?.extensions?.display_message) {
        throw Error(displayError.extensions.display_message);
      }

      throw err;
    }
  }

  private convertCriteria(criteria: any) {
    const result: any = {};

    for (const key in criteria) {
      if (
        criteria[key] !== null &&
        criteria[key] !== undefined &&
        criteria[key] !== ""
      ) {
        result[key] = {
          eq: criteria[key],
        };
      }
    }

    return result;
  }

  private formatCostumeTypeTable(data: any[]) {
    if (!Array.isArray(data)) return [];

    return data.map((item: any) => {
      const features = Array.isArray(item?.costumeFeatures)
        ? item.costumeFeatures
        : [];

      const attributes = features.flatMap((feature: any) =>
        Array.isArray(feature?.costumeAttributes)
          ? feature.costumeAttributes
          : [],
      );

      const values = attributes.flatMap((attribute: any) =>
        Array.isArray(attribute?.costumeValues) ? attribute.costumeValues : [],
      );

      return {
        id: item?.id,
        sectionOptionType: item?.name ?? "-",

        feature:
          features
            .map((feature: any) => feature?.name)
            .filter(Boolean)
            .join(", ") || "-",

        attribute:
          attributes
            .map((attribute: any) => attribute?.name)
            .filter(Boolean)
            .join(", ") || "-",

        value:
          values
            .map((value: any) => value?.name)
            .filter(Boolean)
            .join(", ") || "-",

        defaultInvites: item?.defaultInvites ?? 0,
        sectionSpecificInvites: item?.sectionSpecificInvites ?? 0,
        specialInvites: item?.specialInvites ?? 0,

        status: this.getFormattedStatus(item?.status),
      };
    });
  }
  private getFormattedStatus(status: string) {
    switch (status) {
      case "active":
        return "Active";
      case "inActive":
        return "In Active";
      case "inactive":
        return "In Active";
      case "hidden":
        return "Hidden";
      default:
        return "-";
    }
  }
}
